import SideNavbar from "../SideNavbar";
import { useAppSelector } from "../../redux/store";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { db } from '../../firebaseConfig';
import { 
    collection, 
    query, 
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    where,
    serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../../AuthContext';
import Toast from '../../components/Toast';

interface User {
    id: string;
    email: string;
    name: string;
    role?: string;
    companyId: string;
    photoURL?: string;
}

interface Source {
    id: string;
    name: string; // For UI display, derived from source field
    channel: string; // Channel field from document
    source: string; // Source field from document
}

interface Campaign {
    id: string;
    name: string; // For UI display, derived from campaign field
    campaign: string; // The actual campaign name in document
    isActive: boolean; // To filter active campaigns only
}

interface Webhook {
    id: string;
    name: string;
    webhookSecret: string;
    assignedUsers: string[];
    sourceId: string;
    campaignId: string;
    companyId: string;
    createdAt?: any;
    updatedAt?: any;
}

const Webhooks = () => {
    const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [companyUsers, setCompanyUsers] = useState<User[]>([]);
    const [sources, setSources] = useState<Source[]>([]);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [webhooks, setWebhooks] = useState<Webhook[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Modal state
    const [showModal, setShowModal] = useState(false);
    
    // Form state
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentWebhookId, setCurrentWebhookId] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [webhookSecret, setWebhookSecret] = useState('');
    const [selectedSourceId, setSelectedSourceId] = useState('');
    const [selectedCampaignId, setSelectedCampaignId] = useState('');
    const [sourceSearch, setSourceSearch] = useState('');
    const [campaignSearch, setCampaignSearch] = useState('');
    const [showSourceDropdown, setShowSourceDropdown] = useState(false);
    const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
    
    // Keyboard navigation state
    const [highlightedSourceIndex, setHighlightedSourceIndex] = useState(-1);
    const [highlightedCampaignIndex, setHighlightedCampaignIndex] = useState(-1);
    const sourceDropdownRef = useRef<HTMLDivElement>(null);
    const campaignDropdownRef = useRef<HTMLDivElement>(null);
    
    // Toast notification
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    
    // Modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [webhookToDelete, setWebhookToDelete] = useState<string | null>(null);
    const [showApiModal, setShowApiModal] = useState(false);
    const [currentApiWebhook, setCurrentApiWebhook] = useState<Webhook | null>(null);

    // User assignment state
    const [selectedUsers, setSelectedUsers] = useState<{[key: string]: boolean}>({});
    const [formAssignedUsers, setFormAssignedUsers] = useState<{[webhookId: string]: string[]}>({});
    const [userSearch, setUserSearch] = useState('');

    // Fetch campaigns and sources data
    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.companyId) return;
            setIsLoading(true);
            
            try {
                // Fetch campaigns
                const campaignsQuery = query(
                    collection(db, 'companies', userData.companyId, 'leadCampaigns')
                );
                
                const campaignsSnapshot = await getDocs(campaignsQuery);
                const campaignList: Campaign[] = [];
                
                campaignsSnapshot.forEach(doc => {
                    const data = doc.data();
                    
                    // Only include active campaigns
                    if (data.isActive === true) {
                        campaignList.push({
                            id: doc.id,
                            name: data.campaign || 'Unnamed Campaign', // Use campaign field as the display name
                            ...data
                        } as Campaign);
                    }
                });
                
                setCampaigns(campaignList);

                // Fetch sources
                const sourcesQuery = query(
                    collection(db, 'companies', userData.companyId, 'leadSources')
                );
                
                const sourcesSnapshot = await getDocs(sourcesQuery);
                const sourceList: Source[] = [];
                
                sourcesSnapshot.forEach(doc => {
                    const data = doc.data();
                    sourceList.push({
                        id: doc.id,
                        name: data.source || 'Unnamed Source', // Use source field as the display name
                        ...data
                    } as Source);
                });

                const usersQuery = query(
                    collection(db, 'users'),
                    where('companyId', '==', userData.companyId)
                );
                
                const usersSnapshot = await getDocs(usersQuery);
                const users: User[] = [];
                
                usersSnapshot.forEach(doc => {
                    users.push({
                        id: doc.id,
                        ...doc.data(),
                    } as User);
                });
                
                setCompanyUsers(users);
                
                setSources(sourceList);

                // Fetch webhooks
                await fetchWebhooks();
            } catch (error) {
                console.error('Error fetching data:', error);
                showToastNotification('Error fetching data. Please try again.', true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [userData?.companyId]);

    // Fetch webhooks for the company
    const fetchWebhooks = async () => {
        if (!userData?.companyId) return;
        
        try {
            const webhooksQuery = query(
                collection(db, 'webhookIntegrations'),
                where('companyId', '==', userData.companyId)
            );
            
            const webhooksSnapshot = await getDocs(webhooksQuery);
            const webhooksList: Webhook[] = [];
            const webhookUsers: {[webhookId: string]: string[]} = {};
            
            webhooksSnapshot.forEach(doc => {
                const data = doc.data();
                const webhook = {
                    id: doc.id,
                    ...data,
                    assignedUsers: data.assignedUsers || []
                } as Webhook;
                
                webhooksList.push(webhook);
                
                // Store assigned users for display in UI
                webhookUsers[doc.id] = webhook.assignedUsers || [];
            });
            
            setWebhooks(webhooksList);
            setFormAssignedUsers(webhookUsers);
        } catch (error) {
            console.error('Error fetching webhooks:', error);
            showToastNotification('Error fetching webhooks. Please try again.', true);
        }
    };

    // Generate unique webhook secret
    const generateWebhookSecret = () => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const length = 32;
        let result = '';
        
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        
        return result;
    };

    // Reset form fields
    const resetForm = () => {
        setName('');
        setWebhookSecret(generateWebhookSecret());
        setSelectedSourceId('');
        setSelectedCampaignId('');
        setSourceSearch('');
        setCampaignSearch('');
        setHighlightedSourceIndex(-1);
        setHighlightedCampaignIndex(-1);
        setIsEditMode(false);
        setCurrentWebhookId(null);
        setSelectedUsers({}); // Reset user selection
        setUserSearch(''); // Reset user search
    };

    // Show toast notification
    const showToastNotification = (message: string, isError: boolean = false) => {
        setToastMessage(message);
        setIsErrorToast(isError);
        setShowToast(true);
        
        // Auto-hide toast after 3 seconds
        setTimeout(() => {
            setShowToast(false);
        }, 3000);
    };

    // Handle form submission (create or update webhook)
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!userData?.companyId) {
            showToastNotification('You must be logged in to perform this action.', true);
            return;
        }
        
        if (!name || !webhookSecret || !selectedSourceId || !selectedCampaignId) {
            showToastNotification('All fields are required.', true);
            return;
        }

        // Validate that selected source and campaign exist
        const sourceExists = sources.some(source => source.id === selectedSourceId);
        const campaignExists = campaigns.some(campaign => campaign.id === selectedCampaignId);

        if (!sourceExists) {
            showToastNotification('Please select a valid source from the dropdown.', true);
            return;
        }

        if (!campaignExists) {
            showToastNotification('Please select a valid campaign from the dropdown.', true);
            return;
        }
        
        if (webhookSecret.length < 10) {
            showToastNotification('Webhook secret must be at least 10 characters long.', true);
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            // Check for duplicate webhook secret within the company
            const webhookSecretQuery = query(
                collection(db, 'webhookIntegrations'),
                where('companyId', '==', userData.companyId),
                where('webhookSecret', '==', webhookSecret)
            );
            
            const webhookSecretSnapshot = await getDocs(webhookSecretQuery);
            
            // If updating, exclude the current webhook from duplicate check
            const hasDuplicate = isEditMode
                ? webhookSecretSnapshot.docs.some(doc => doc.id !== currentWebhookId)
                : !webhookSecretSnapshot.empty;
            
            if (hasDuplicate) {
                showToastNotification('This webhook secret is already in use. Please generate a new one.', true);
                setIsSubmitting(false);
                return;
            }
            
            // Get selected user IDs
            const userIds = Object.keys(selectedUsers).filter(id => selectedUsers[id]);
            
            if (isEditMode && currentWebhookId) {
                // Update existing webhook
                await updateDoc(doc(db, 'webhookIntegrations', currentWebhookId), {
                    name,
                    webhookSecret,
                    sourceId: selectedSourceId,
                    campaignId: selectedCampaignId,
                    assignedUsers: userIds,
                    companyId: userData.companyId,
                    updatedAt: serverTimestamp()
                });
                
                // Update formAssignedUsers state
                setFormAssignedUsers(prev => ({
                    ...prev,
                    [currentWebhookId]: userIds
                }));
                
                showToastNotification('Webhook updated successfully.');
                
                // Refresh webhooks list and close modal
                await fetchWebhooks();
                setShowModal(false);
            } else {
                // Create new webhook
                const docRef = await addDoc(collection(db, 'webhookIntegrations'), {
                    name,
                    webhookSecret,
                    sourceId: selectedSourceId,
                    campaignId: selectedCampaignId,
                    assignedUsers: userIds,
                    companyId: userData.companyId,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                
                // Update formAssignedUsers state
                setFormAssignedUsers(prev => ({
                    ...prev,
                    [docRef.id]: userIds
                }));
                
                showToastNotification('Webhook created successfully.');
                
                // Refresh webhooks list and reset form
                await fetchWebhooks();
                resetForm();
                setShowModal(false);
            }
        } catch (error) {
            console.error('Error saving webhook:', error);
            showToastNotification('Error saving webhook. Please try again.', true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle editing a webhook
    const handleEdit = (webhook: Webhook) => {
        setIsEditMode(true);
        setCurrentWebhookId(webhook.id);
        setName(webhook.name);
        setWebhookSecret(webhook.webhookSecret);
        setSelectedSourceId(webhook.sourceId);
        setSelectedCampaignId(webhook.campaignId);
        
        // Set source search to display the source name in the input
        const source = sources.find(s => s.id === webhook.sourceId);
        setSourceSearch(source ? source.name : '');
        
        // Set campaign search to display the campaign name in the input
        const campaign = campaigns.find(c => c.id === webhook.campaignId);
        setCampaignSearch(campaign ? campaign.name : '');

        // Set selected users from assigned users
        const userSelectionMap: {[key: string]: boolean} = {};
        (webhook.assignedUsers || []).forEach(userId => {
            userSelectionMap[userId] = true;
        });
        setSelectedUsers(userSelectionMap);
        
        setShowModal(true);
    };

    // Handle deleting a webhook
    const handleDelete = async () => {
        if (!webhookToDelete) return;
        
        setIsSubmitting(true);
        
        try {
            await deleteDoc(doc(db, 'webhookIntegrations', webhookToDelete));
            
            showToastNotification('Webhook deleted successfully.');
            
            // Refresh webhooks list
            await fetchWebhooks();
            
            // Close delete modal
            setShowDeleteModal(false);
            setWebhookToDelete(null);
        } catch (error) {
            console.error('Error deleting webhook:', error);
            showToastNotification('Error deleting webhook. Please try again.', true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Open delete confirmation modal
    const openDeleteModal = (webhookId: string) => {
        setWebhookToDelete(webhookId);
        setShowDeleteModal(true);
    };

    // Generate new webhook secret
    const handleGenerateSecret = () => {
        setWebhookSecret(generateWebhookSecret());
    };

    // Initialize webhook secret on component mount
    useEffect(() => {
        setWebhookSecret(generateWebhookSecret());
    }, []);

    const handleGoBack = () => {
        navigate('/integrations');
    };

    // Handle focus and blur events for source dropdown
    const handleSourceFocus = () => {
        setShowSourceDropdown(true);
    };
    
    const handleSourceBlur = (e: React.FocusEvent) => {
        // Check if the related target is within the dropdown
        const dropdown = document.getElementById('source-dropdown');
        if (dropdown && dropdown.contains(e.relatedTarget as Node)) {
            return; // Don't hide if focus moved to an element inside the dropdown
        }
        
        // Delay hiding the dropdown to allow for click events to register
        setTimeout(() => {
            setShowSourceDropdown(false);
        }, 200);
    };
    
    // Handle focus and blur events for campaign dropdown
    const handleCampaignFocus = () => {
        setShowCampaignDropdown(true);
    };
    
    const handleCampaignBlur = (e: React.FocusEvent) => {
        // Check if the related target is within the dropdown
        const dropdown = document.getElementById('campaign-dropdown');
        if (dropdown && dropdown.contains(e.relatedTarget as Node)) {
            return; // Don't hide if focus moved to an element inside the dropdown
        }
        
        // Delay hiding the dropdown to allow for click events to register
        setTimeout(() => {
            setShowCampaignDropdown(false);
        }, 200);
    };

    // Handle clicking on source input
    const handleSourceClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setShowSourceDropdown(true);
    };

    // Handle clicking on campaign input
    const handleCampaignClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setShowCampaignDropdown(true);
    };

    // Handle click in source dropdown without closing
    const handleSourceDropdownClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    // Handle click in campaign dropdown without closing
    const handleCampaignDropdownClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    // Handle keyboard navigation for source dropdown
    const handleSourceKeyboardNavigation = (e: KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            const nextIndex = highlightedSourceIndex + 1;
            if (nextIndex < sources.length) {
                setHighlightedSourceIndex(nextIndex);
            }
        } else if (e.key === 'ArrowUp') {
            const prevIndex = highlightedSourceIndex - 1;
            if (prevIndex >= 0) {
                setHighlightedSourceIndex(prevIndex);
            }
        } else if (e.key === 'Enter') {
            if (highlightedSourceIndex >= 0) {
                const selectedSource = sources[highlightedSourceIndex];
                setSelectedSourceId(selectedSource.id);
                setShowSourceDropdown(false);
                setSourceSearch(selectedSource.name);
            }
        }
    };

    // Handle keyboard navigation for campaign dropdown
    const handleCampaignKeyboardNavigation = (e: KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            const nextIndex = highlightedCampaignIndex + 1;
            if (nextIndex < campaigns.length) {
                setHighlightedCampaignIndex(nextIndex);
            }
        } else if (e.key === 'ArrowUp') {
            const prevIndex = highlightedCampaignIndex - 1;
            if (prevIndex >= 0) {
                setHighlightedCampaignIndex(prevIndex);
            }
        } else if (e.key === 'Enter') {
            if (highlightedCampaignIndex >= 0) {
                const selectedCampaign = campaigns[highlightedCampaignIndex];
                setSelectedCampaignId(selectedCampaign.id);
                setShowCampaignDropdown(false);
                setCampaignSearch(selectedCampaign.name);
            }
        }
    };

    // Open create webhook modal
    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    // Close webhook modal
    const closeModal = () => {
        resetForm();
        setShowModal(false);
    };

    // Show API example modal
    const openApiModal = (webhook: Webhook) => {
        setCurrentApiWebhook(webhook);
        setShowApiModal(true);
    };

    // Close API example modal
    const closeApiModal = () => {
        setCurrentApiWebhook(null);
        setShowApiModal(false);
    };

    // Handle selecting or deselecting all users
    const handleSelectAllUsers = (select: boolean) => {
        const newSelectedUsers: {[key: string]: boolean} = {};
        
        // If we're filtering users, only select/deselect the filtered ones
        const usersToUpdate = userSearch.trim() ? 
            companyUsers.filter(user => 
                (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
            ) : 
            companyUsers;
        
        // Copy existing selections
        Object.keys(selectedUsers).forEach(id => {
            newSelectedUsers[id] = selectedUsers[id];
        });
        
        // Update with new selections
        usersToUpdate.forEach(user => {
            newSelectedUsers[user.id] = select;
        });
        
        setSelectedUsers(newSelectedUsers);
    };

    // Add effect to handle clicking outside dropdowns
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Close source dropdown if click is outside
            if (showSourceDropdown && 
                sourceDropdownRef.current && 
                !sourceDropdownRef.current.contains(event.target as Node)) {
                setShowSourceDropdown(false);
            }
            
            // Close campaign dropdown if click is outside
            if (showCampaignDropdown && 
                campaignDropdownRef.current && 
                !campaignDropdownRef.current.contains(event.target as Node)) {
                setShowCampaignDropdown(false);
            }
        };
        
        // Add event listener
        document.addEventListener('mousedown', handleClickOutside);
        
        // Clean up
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSourceDropdown, showCampaignDropdown]);

    return (
        <div className="min-h-screen bg-gray-200">
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>
            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-8 rounded-3xl shadow-md">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <button 
                                onClick={handleGoBack}
                                className="text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Back to Integrations
                            </button>
                            <h1 className="text-2xl font-bold text-gray-900">Webhook Integration</h1>
                            <p className="text-gray-600 mt-2">Connect your Webhook with the ScholarCRM to receive and add leads in real-time</p>
                        </div>
                        <div className="flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 48 48">
                                <path fill="#37474f" d="M35,37c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S37.2,37,35,37z"></path><path fill="#37474f" d="M35,43c-3,0-5.9-1.4-7.8-3.7l3.1-2.5c1.1,1.4,2.9,2.3,4.7,2.3c3.3,0,6-2.7,6-6s-2.7-6-6-6 c-1,0-2,0.3-2.9,0.7l-1.7,1L23.3,16l3.5-1.9l5.3,9.4c1-0.3,2-0.5,3-0.5c5.5,0,10,4.5,10,10S40.5,43,35,43z"></path><path fill="#37474f" d="M14,43C8.5,43,4,38.5,4,33c0-4.6,3.1-8.5,7.5-9.7l1,3.9C9.9,27.9,8,30.3,8,33c0,3.3,2.7,6,6,6 s6-2.7,6-6v-2h15v4H23.8C22.9,39.6,18.8,43,14,43z"></path><path fill="#e91e63" d="M14,37c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S16.2,37,14,37z"></path><path fill="#37474f" d="M25,19c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S27.2,19,25,19z"></path><path fill="#e91e63" d="M15.7,34L12.3,32l5.9-9.7c-2-1.9-3.2-4.5-3.2-7.3c0-5.5,4.5-10,10-10c5.5,0,10,4.5,10,10 c0,0.9-0.1,1.7-0.3,2.5l-3.9-1c0.1-0.5,0.2-1,0.2-1.5c0-3.3-2.7-6-6-6s-6,2.7-6,6c0,2.1,1.1,4,2.9,5.1l1.7,1L15.7,34z"></path>
                            </svg>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="mt-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-semibold">Your Webhooks</h2>
                            <button
                                onClick={openCreateModal}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Create Webhook
                            </button>
                        </div>
                        
                        {/* Webhooks List */}
                        <div>
                            {isLoading ? (
                                <div className="flex justify-center items-center p-8">
                                    <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                            ) : webhooks.length === 0 ? (
                                <div className="bg-gray-50 rounded-lg p-8 text-center">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    <p className="text-gray-500 mt-2">No webhooks found. Create your first one with the button above!</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Name
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Source
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Campaign
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Assigned Users
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {webhooks.map((webhook) => {
                                                // Find the source and campaign names
                                                const source = sources.find(s => s.id === webhook.sourceId);
                                                const campaign = campaigns.find(c => c.id === webhook.campaignId);
                                                
                                                return (
                                                    <tr key={webhook.id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {webhook.name}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {source?.name || 'Unknown source'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {campaign?.name || 'Unknown campaign'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            <div className="text-left relative group">
                                                                {formAssignedUsers[webhook.id] && formAssignedUsers[webhook.id].length > 0 ? (
                                                                    <div>
                                                                        <div className="flex items-center -space-x-2">
                                                                            {formAssignedUsers[webhook.id].slice(0, 3).map((userId) => {
                                                                                const user = companyUsers.find(u => u.id === userId);
                                                                                return user ? (
                                                                                    <div key={userId} className="h-8 w-8 rounded-full bg-white ring-2 ring-white overflow-hidden">
                                                                                        {user.photoURL ? (
                                                                                            <img src={user.photoURL} alt={user.name || 'User'} className="h-full w-full object-cover" />
                                                                                        ) : (
                                                                                            <div className="h-full w-full bg-blue-100 flex items-center justify-center">
                                                                                                <span className="text-blue-800 font-medium text-xs">
                                                                                                    {user.name ? user.name.split(' ').map(n => n[0]).join('') : user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                ) : null;
                                                                            })}
                                                                            {formAssignedUsers[webhook.id].length > 3 && (
                                                                                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-white">
                                                                                    <span className="text-gray-700 text-xs font-medium">+{formAssignedUsers[webhook.id].length - 3}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        
                                                                        {/* Tooltip that appears on hover */}
                                                                        <div className="hidden group-hover:block absolute z-10 mt-1 w-48 bg-white shadow-lg rounded-md p-2 text-xs border border-gray-200 left-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                                            <div className="py-1 font-medium text-gray-600 border-b border-gray-100 mb-1">Assigned users:</div>
                                                                            <div className="max-h-40 overflow-y-auto">
                                                                                {formAssignedUsers[webhook.id].map(userId => {
                                                                                    const user = companyUsers.find(u => u.id === userId);
                                                                                    return user ? (
                                                                                        <div key={userId} className="flex items-center py-1">
                                                                                            <div className="mr-2 h-5 w-5 rounded-full bg-blue-100 flex-shrink-0 overflow-hidden">
                                                                                                {user.photoURL ? (
                                                                                                    <img src={user.photoURL} alt={user.name} className="h-full w-full object-cover" />
                                                                                                ) : (
                                                                                                    <div className="h-full w-full flex items-center justify-center">
                                                                                                        <span className="text-blue-800 text-xs">
                                                                                                            {user.name ? user.name.charAt(0).toUpperCase() : user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                            <div className="truncate">
                                                                                                <div className="font-medium">{user.name || 'Unnamed'}</div>
                                                                                                <div className="text-gray-500 text-xs">{user.email}</div>
                                                                                            </div>
                                                                                        </div>
                                                                                    ) : null;
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-xs text-gray-500 flex items-center">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                        </svg>
                                                                        No users assigned
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                            <div className="flex justify-end space-x-2">
                                                                <button
                                                                    onClick={() => openApiModal(webhook)}
                                                                    className="text-purple-600 hover:text-purple-900"
                                                                    title="API Information"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleEdit(webhook)}
                                                                    className="text-blue-600 hover:text-blue-900"
                                                                    title="Edit webhook"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={() => openDeleteModal(webhook.id)}
                                                                    className="text-red-600 hover:text-red-900"
                                                                    title="Delete webhook"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Create/Edit Webhook Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {isEditMode ? 'Edit Webhook' : 'Create New Webhook'}
                            </h2>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit}>
                            <div className="space-y-6 pt-5 pb-3 text-left sm:space-y-4">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Webhook Name
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter a descriptive name"
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label htmlFor="webhookSecret" className="block text-sm font-medium text-gray-700 mb-1">
                                        Webhook Secret
                                    </label>
                                    <div className="flex">
                                        <input
                                            type="text"
                                            id="webhookSecret"
                                            value={webhookSecret}
                                            onChange={(e) => setWebhookSecret(e.target.value)}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter a secret key"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={handleGenerateSecret}
                                            className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 text-sm leading-4 font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        >
                                            Generate
                                        </button>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">
                                        This secret will be used to authenticate webhook requests. Keep it confidential.
                                    </p>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Source */}
                                    <div>
                                        <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-1">
                                            Lead Source
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                id="source"
                                                value={sourceSearch}
                                                onChange={(e) => setSourceSearch(e.target.value)}
                                                onFocus={handleSourceFocus}
                                                onBlur={handleSourceBlur}
                                                onClick={handleSourceClick}
                                                onKeyDown={handleSourceKeyboardNavigation}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Search for a source"
                                                required
                                            />
                                            {showSourceDropdown && (
                                                <div 
                                                    id="source-dropdown"
                                                    ref={sourceDropdownRef}
                                                    className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-40 min-w-[200px]"
                                                    onClick={handleSourceDropdownClick}
                                                >
                                                    {sources.length === 0 ? (
                                                        <div className="p-3 text-sm text-gray-500">No sources found.</div>
                                                    ) : (
                                                        sources
                                                            .filter(source => source.name.toLowerCase().includes(sourceSearch.toLowerCase()))
                                                            .map((source, index) => (
                                                                <div
                                                                    key={source.id}
                                                                    className={`p-3 text-sm hover:bg-gray-50 cursor-pointer ${index === highlightedSourceIndex ? 'bg-blue-50' : ''}`}
                                                                    onClick={() => {
                                                                        setSelectedSourceId(source.id);
                                                                        setSourceSearch(source.name);
                                                                        setShowSourceDropdown(false);
                                                                    }}
                                                                >
                                                                    {source.name}
                                                                </div>
                                                            ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Campaign */}
                                    <div>
                                        <label htmlFor="campaign" className="block text-sm font-medium text-gray-700 mb-1">
                                            Lead Campaign
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                id="campaign"
                                                value={campaignSearch}
                                                onChange={(e) => setCampaignSearch(e.target.value)}
                                                onFocus={handleCampaignFocus}
                                                onBlur={handleCampaignBlur}
                                                onClick={handleCampaignClick}
                                                onKeyDown={handleCampaignKeyboardNavigation}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Search for a campaign"
                                                required
                                            />
                                            {showCampaignDropdown && (
                                                <div 
                                                    id="campaign-dropdown"
                                                    ref={campaignDropdownRef}
                                                    className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-40 min-w-[200px]"
                                                    onClick={handleCampaignDropdownClick}
                                                >
                                                    {campaigns.length === 0 ? (
                                                        <div className="p-3 text-sm text-gray-500">No campaigns found.</div>
                                                    ) : (
                                                        campaigns
                                                            .filter(campaign => campaign.name.toLowerCase().includes(campaignSearch.toLowerCase()))
                                                            .map((campaign, index) => (
                                                                <div
                                                                    key={campaign.id}
                                                                    className={`p-3 text-sm hover:bg-gray-50 cursor-pointer ${index === highlightedCampaignIndex ? 'bg-blue-50' : ''}`}
                                                                    onClick={() => {
                                                                        setSelectedCampaignId(campaign.id);
                                                                        setCampaignSearch(campaign.name);
                                                                        setShowCampaignDropdown(false);
                                                                    }}
                                                                >
                                                                    {campaign.name}
                                                                </div>
                                                            ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* User Assignment Section */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Assign Users
                                    </label>
                                    <div className="border border-gray-300 rounded-md shadow-sm overflow-hidden">
                                        <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                                            <div className="relative flex-1">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    placeholder="Search users..."
                                                    className="pl-10 pr-3 py-1.5 w-full text-xs text-gray-900 bg-white border-none focus:outline-none focus:ring-0"
                                                    value={userSearch}
                                                    onChange={(e) => setUserSearch(e.target.value)}
                                                />
                                            </div>
                                            <div className="flex items-center space-x-2 ml-2">
                                                <button 
                                                    type="button"
                                                    className="text-xs text-blue-600 font-medium hover:text-blue-800"
                                                    onClick={() => handleSelectAllUsers(true)}
                                                >
                                                    Select All
                                                </button>
                                                <span className="text-gray-300">|</span>
                                                <button 
                                                    type="button"
                                                    className="text-xs text-gray-600 font-medium hover:text-gray-800"
                                                    onClick={() => handleSelectAllUsers(false)}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>

                                        <div className="max-h-48 overflow-y-auto p-2">
                                            {companyUsers.length > 0 ? (
                                                companyUsers
                                                    .filter(user => 
                                                        !userSearch || 
                                                        (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                        (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                        (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
                                                    )
                                                    .map(user => (
                                                        <div key={user.id} className="flex items-center py-1 px-2 hover:bg-gray-50 rounded">
                                                            <input
                                                                type="checkbox"
                                                                id={`user-${user.id}`}
                                                                checked={!!selectedUsers[user.id]}
                                                                onChange={() => {
                                                                    setSelectedUsers(prev => ({
                                                                        ...prev,
                                                                        [user.id]: !prev[user.id]
                                                                    }));
                                                                }}
                                                                className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                            />
                                                            <label htmlFor={`user-${user.id}`} className="ml-2 flex items-center cursor-pointer flex-1 min-w-0">
                                                                <div className="mr-2 h-6 w-6 rounded-full bg-blue-100 flex-shrink-0 overflow-hidden">
                                                                    {user.photoURL ? (
                                                                        <img src={user.photoURL} alt={user.name} className="h-full w-full object-cover" />
                                                                    ) : (
                                                                        <div className="h-full w-full flex items-center justify-center">
                                                                            <span className="text-blue-800 font-medium text-xs">
                                                                                {user.name ? user.name.split(' ').map(n => n[0]).join('') : user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="truncate">
                                                                    <div className="text-xs font-medium">{user.name || 'Unnamed'}</div>
                                                                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                                                                </div>
                                                                {user.role && (
                                                                    <span className="ml-auto text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-md">
                                                                        {user.role}
                                                                    </span>
                                                                )}
                                                            </label>
                                                        </div>
                                                    ))
                                            ) : (
                                                <div className="text-center py-6">
                                                    <svg className="h-8 w-8 text-gray-400 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                    <p className="text-xs text-gray-500">No company users found</p>
                                                </div>
                                            )}
                                            {userSearch && companyUsers.filter(user => 
                                                (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
                                            ).length === 0 && (
                                                <div className="text-center py-4 text-xs text-gray-500">
                                                    No users found matching "{userSearch}"
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
                                            {Object.keys(selectedUsers).filter(id => selectedUsers[id]).length} users selected
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 mt-8">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Processing...
                                        </>
                                    ) : (
                                        <>{isEditMode ? 'Update Webhook' : 'Create Webhook'}</>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete this webhook? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete Webhook'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* API Example Modal */}
            {showApiModal && currentApiWebhook && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">
                                API Integration for: {currentApiWebhook.name}
                            </h2>
                            <button
                                onClick={closeApiModal}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="overflow-y-auto p-6 space-y-6 flex-grow">
                            <div>
                                <h3 className="text-md font-medium text-gray-900 mb-2">Example Curl Request</h3>
                                <div className="bg-gray-50 p-4 rounded-md">
                                    <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
{`curl -X POST https://us-central1-scholar-crm-test-58d6c.cloudfunctions.net/processWebhook \\
  -H "Content-Type: application/json" \\
  -d '{
  "companyId": "${userData?.companyId || '[YOUR_COMPANY_ID]'}",
  "secret": "${currentApiWebhook.webhookSecret}",
  "leads": [
    {
      "name": "John Doe",
      "number": "+919876543210"
    },
    {
      "name": "Jane Smith",
      "number": "+918765432109"
    }
  ]
}'`}
                                    </pre>
                                </div>
                                <button 
                                    className="mt-2 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    onClick={() => {
                                        navigator.clipboard.writeText(
                                            `curl -X POST https://us-central1-scholar-crm-test-58d6c.cloudfunctions.net/processWebhook \\
  -H "Content-Type: application/json" \\
  -d '{
  "companyId": "${userData?.companyId || '[YOUR_COMPANY_ID]'}",
  "secret": "${currentApiWebhook.webhookSecret}",
  "leads": [
    {
      "name": "John Doe",
      "number": "+919876543210"
    },
    {
      "name": "Jane Smith",
      "number": "+918765432109"
    }
  ]
}'`
                                        );
                                        showToastNotification("Copied to clipboard!");
                                    }}
                                >
                                    Copy to Clipboard
                                </button>
                            </div>
                            
                            <div>
                                <h3 className="text-md font-medium text-gray-900 mb-2">API Specification</h3>
                                <div className="bg-gray-50 p-4 rounded-md">
                                    <h4 className="text-sm font-semibold mb-2">Endpoint</h4>
                                    <p className="text-sm mb-4">POST https://us-central1-scholar-crm-test-58d6c.cloudfunctions.net/processWebhook</p>
                                    
                                    <h4 className="text-sm font-semibold mb-2">Headers</h4>
                                    <p className="text-sm mb-4">Content-Type: application/json</p>
                                    
                                    <h4 className="text-sm font-semibold mb-2">Request Body</h4>
                                    <pre className="text-sm mb-4">
{`{
  "companyId": "string", // Your company ID
  "secret": "string",    // Your webhook secret
  "leads": [             // Array of lead objects
    {
      "name": "string",  // Lead's full name
      "number": "string", // Phone number (must start with +)
      "remark": "string"  // Optional remarks or notes about the lead
    }
  ]
}`}
                                    </pre>
                                    
                                    <h4 className="text-sm font-semibold mb-2">Response (Success)</h4>
                                    <pre className="text-sm mb-4">
{`{
  "success": true,
  "message": "Successfully added X leads. Skipped Y duplicate leads.",
  "leadIds": ["id1", "id2", ...],
  "skippedCount": 0,
  "skippedNumbers": []
}`}
                                    </pre>
                                    
                                    <h4 className="text-sm font-semibold mb-2">Response (Error)</h4>
                                    <pre className="text-sm">
{`{
  "error": "Error Type",
  "message": "Error message",
  "details": [] // Additional error details if applicable
}`}
                                    </pre>
                                </div>
                            </div>
                            
                            <div>
                                <h3 className="text-md font-medium text-gray-900 mb-2">Notes</h3>
                                <ul className="list-disc list-inside text-sm space-y-1 text-gray-700">
                                    <li>All phone numbers must start with a + symbol followed by country code</li>
                                    <li>Duplicate phone numbers are automatically skipped</li>
                                    <li>Batches of up to 500 leads are processed in a single request</li>
                                    <li>The API will respond with appropriate error codes and messages</li>
                                    <li>Keep your webhook secret confidential</li>
                                    <li>The 'remark' field is optional and can be used to add remarks or notes about the lead</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast notification */}
            {showToast && (
                <Toast 
                    message={toastMessage}
                    isError={isErrorToast}
                    onClose={() => setShowToast(false)}
                    show={showToast}
                />
            )}
        </div>
    );
};

export default Webhooks;